/**
 * PlanetDragHighlightService - Handles planet highlighting during drag and time motion modes
 *
 * Self-initializing singleton that subscribes to planet drag and time motion events
 * and applies visual highlighting:
 *
 * Planet Drag mode:
 * - Dragged planet: golden circle background
 * - Text colors: Ch=green, Lg=red, Sy=copper, others=black
 *
 * Time Motion mode:
 * - All planets except Lagna get text colors: Ch=green, Sy=copper, others=black
 * - Lagna keeps original color (stays unchanged)
 */

import * as d3 from 'd3';
import { devLog } from '../utils/devLog';
import { globalEventStream } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import type { BaseEvent, EventSubscription } from '../components/common/AstrologyChart/universal_architecture/types/EventTypes';
import { DEFAULT_PRESET } from '../config/chartColorPresets';

const DEBUG = true;

// Planet text colors during drag mode
const PLANET_TEXT_COLORS: Record<string, string> = {
    'Ch': '#228B22',      // Chandra - green
    'Lg': '#DC143C',      // Lagna - red
    'Sy': '#B87333',      // Surya - copper
};
const DEFAULT_TEXT_COLOR = '#000000';  // Black for others

type HighlightMode = 'drag' | 'time-motion';

class PlanetDragHighlightService {
    private static instance: PlanetDragHighlightService | null = null;
    private subscriptions: EventSubscription[] = [];
    private activeHighlights: Map<string, { planet?: string; mode: HighlightMode }> = new Map(); // chartId -> highlight info

    private constructor() {
        this.setupSubscriptions();
        if (DEBUG) devLog('[PlanetDrag:Highlight] 🎨 Service initialized');
    }

    public static getInstance(): PlanetDragHighlightService {
        if (!PlanetDragHighlightService.instance) {
            PlanetDragHighlightService.instance = new PlanetDragHighlightService();
        }
        return PlanetDragHighlightService.instance;
    }

    private setupSubscriptions(): void {
        // Subscribe to drag ready - apply highlight
        this.subscriptions.push(
            globalEventStream.subscribe('planet.drag.ready', (event: BaseEvent) => {
                this.handleDragStart(event);
            })
        );

        // Subscribe to drag end - remove highlight
        this.subscriptions.push(
            globalEventStream.subscribe('planet.drag.end', (event: BaseEvent) => {
                this.handleDragEnd(event);
            })
        );

        // Subscribe to drag cancel - remove highlight
        this.subscriptions.push(
            globalEventStream.subscribe('planet.drag.cancel', (event: BaseEvent) => {
                this.handleDragEnd(event);
            })
        );

        // Subscribe to drag confirmed - remove highlight
        this.subscriptions.push(
            globalEventStream.subscribe('planet.drag.confirmed', (event: BaseEvent) => {
                this.handleDragEnd(event);
            })
        );

        // ===========================================================================
        // Time Motion subscriptions
        // ===========================================================================

        // Subscribe to time motion overlay opened - apply highlight (all planets except Lagna)
        this.subscriptions.push(
            globalEventStream.subscribe('time.motion.ready', (event: BaseEvent) => {
                this.handleTimeMotionStart(event);
            })
        );

        // Subscribe to time motion overlay closed - remove highlight
        this.subscriptions.push(
            globalEventStream.subscribe('time.motion.overlay.closed', (event: BaseEvent) => {
                this.handleTimeMotionEnd(event);
            })
        );

        if (DEBUG) devLog('[PlanetDrag:Highlight] 📡 Subscribed to drag and time motion events');
    }

    /**
     * Handle drag start - apply planet text colors and golden background for dragged planet
     */
    private handleDragStart(event: BaseEvent): void {
        const chartId = event.metadata?.chartId as string;
        const draggedPlanet = event.metadata?.planet as string;

        if (!chartId || !draggedPlanet) {
            if (DEBUG) devLog('[PlanetDrag:Highlight] ⚠️ Missing chartId or planet in event');
            return;
        }

        if (DEBUG) devLog(`[PlanetDrag:Highlight] ✨ Applying highlight for ${draggedPlanet} on chart ${chartId}`);

        // Remove any existing highlights for this chart
        this.removeHighlight(chartId);

        const dragBackgroundColor = DEFAULT_PRESET.planetSprites.dragHighlightColor;

        // Get the parent group (lines-overlay) - use ID selector
        const parent = d3.select(`#lines-overlay-${chartId}`);
        if (parent.empty()) {
            if (DEBUG) devLog(`[PlanetDrag:Highlight] ⚠️ Lines overlay not found for chart ${chartId}`);
            return;
        }

        // Find all planet sprites and style them
        const allSprites = parent.selectAll('.planet-sprite');

        if (DEBUG) devLog(`[PlanetDrag:Highlight] Found ${allSprites.size()} sprites`);

        allSprites.each(function() {
            const sprite = d3.select(this);

            // Get planet name from data attribute
            const planetName = sprite.attr('data-planet');
            if (!planetName) return;
            const isDragged = planetName === draggedPlanet;

            // Find text element within sprite
            const textEl = sprite.select('text');
            if (textEl.empty()) return;

            // Store original color for restoration
            const originalFill = textEl.attr('fill');
            if (originalFill && !textEl.attr('data-original-fill')) {
                textEl.attr('data-original-fill', originalFill);
            }

            // Set text color
            const textColor = PLANET_TEXT_COLORS[planetName] || DEFAULT_TEXT_COLOR;
            textEl.attr('fill', textColor);

            // Change circle background for dragged planet only
            if (isDragged) {
                const circleEl = sprite.select('circle');
                if (!circleEl.empty()) {
                    // Store original fill
                    const originalCircleFill = circleEl.attr('fill');
                    if (originalCircleFill && !circleEl.attr('data-original-fill')) {
                        circleEl.attr('data-original-fill', originalCircleFill);
                    }
                    // Set golden background
                    circleEl.attr('fill', dragBackgroundColor);
                    if (DEBUG) devLog(`[PlanetDrag:Highlight] 🔵 Changed ${planetName} circle fill to golden`);
                }
            }
        });

        // Store active highlight
        this.activeHighlights.set(chartId, { planet: draggedPlanet, mode: 'drag' });

        if (DEBUG) devLog(`[PlanetDrag:Highlight] ✅ Highlight applied for ${draggedPlanet}`);
    }

    /**
     * Handle time motion start - apply text colors to all planets except Lagna
     */
    private handleTimeMotionStart(event: BaseEvent): void {
        const chartId = event.metadata?.chartId as string;

        if (!chartId) {
            if (DEBUG) devLog('[PlanetDrag:Highlight] ⚠️ Missing chartId in time motion event');
            return;
        }

        if (DEBUG) devLog(`[PlanetDrag:Highlight] ⏱️ Applying time motion highlight for chart ${chartId}`);

        // Remove any existing highlights for this chart
        this.removeHighlight(chartId);

        // Get the parent group (lines-overlay) - use ID selector
        const parent = d3.select(`#lines-overlay-${chartId}`);
        if (parent.empty()) {
            if (DEBUG) devLog(`[PlanetDrag:Highlight] ⚠️ Lines overlay not found for chart ${chartId}`);
            return;
        }

        // Find all planet sprites and style them (except Lagna)
        const allSprites = parent.selectAll('.planet-sprite');

        if (DEBUG) devLog(`[PlanetDrag:Highlight] Found ${allSprites.size()} sprites for time motion`);

        allSprites.each(function() {
            const sprite = d3.select(this);

            // Get planet name from data attribute
            const planetName = sprite.attr('data-planet');
            if (!planetName) return;

            // Skip Lagna - it keeps original color
            if (planetName === 'Lg') return;

            // Find text element within sprite
            const textEl = sprite.select('text');
            if (textEl.empty()) return;

            // Store original color for restoration
            const originalFill = textEl.attr('fill');
            if (originalFill && !textEl.attr('data-original-fill')) {
                textEl.attr('data-original-fill', originalFill);
            }

            // Set text color (Lg excluded, so no red color used here)
            const textColor = PLANET_TEXT_COLORS[planetName] || DEFAULT_TEXT_COLOR;
            textEl.attr('fill', textColor);
        });

        // Store active highlight
        this.activeHighlights.set(chartId, { mode: 'time-motion' });

        if (DEBUG) devLog(`[PlanetDrag:Highlight] ✅ Time motion highlight applied`);
    }

    /**
     * Handle drag end - remove highlights and restore colors
     */
    private handleDragEnd(event: BaseEvent): void {
        const chartId = event.metadata?.chartId as string;
        if (!chartId) return;

        this.removeHighlight(chartId);
    }

    /**
     * Handle time motion end - remove highlights and restore colors
     */
    private handleTimeMotionEnd(event: BaseEvent): void {
        const chartId = event.metadata?.chartId as string;
        if (!chartId) return;

        this.removeHighlight(chartId);
    }

    /**
     * Remove all highlights for a chart and restore original colors
     */
    private removeHighlight(chartId: string): void {
        // Restore original text and circle colors
        const parent = d3.select(`#lines-overlay-${chartId}`);
        if (!parent.empty()) {
            const allSprites = parent.selectAll('.planet-sprite');
            allSprites.each(function() {
                const sprite = d3.select(this);

                // Restore text color
                const textEl = sprite.select('text');
                if (!textEl.empty()) {
                    const originalFill = textEl.attr('data-original-fill');
                    if (originalFill) {
                        textEl.attr('fill', originalFill);
                        textEl.attr('data-original-fill', null);
                    }
                }

                // Restore circle fill
                const circleEl = sprite.select('circle');
                if (!circleEl.empty()) {
                    const originalCircleFill = circleEl.attr('data-original-fill');
                    if (originalCircleFill) {
                        circleEl.attr('fill', originalCircleFill);
                        circleEl.attr('data-original-fill', null);
                    }
                }
            });
            if (DEBUG) devLog(`[PlanetDrag:Highlight] 🔄 Restored original colors`);
        }

        this.activeHighlights.delete(chartId);
    }

    /**
     * Cleanup service
     */
    public destroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
        this.activeHighlights.clear();
        PlanetDragHighlightService.instance = null;
        if (DEBUG) devLog('[PlanetDrag:Highlight] 🧹 Service destroyed');
    }
}

// Auto-initialize on import
export const planetDragHighlightService = PlanetDragHighlightService.getInstance();
